if (!customElements.get('popular-products-carousel')) {
  customElements.define(
    'popular-products-carousel',
    class PopularProductsCarousel extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-carousel-viewport]');
        this.track = this.querySelector('.popular-products__track');
        this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
        this.controls = this.querySelector('[data-carousel-controls]');
        this.previousButton = this.querySelector('[data-carousel-previous]');
        this.nextButton = this.querySelector('[data-carousel-next]');
        this.autoplayEnabled = this.dataset.autoplay === 'true';
        this.autoplayDelay = Number(this.dataset.autoplayDelay) || 5000;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (!this.viewport || this.slides.length < 2) return;

        this.onPrevious = () => this.move(-1, true);
        this.onNext = () => this.move(1, true);
        this.onPointerEnter = () => this.stopAutoplay();
        this.onPointerLeave = () => this.startAutoplay();
        this.onFocusIn = () => this.stopAutoplay();
        this.onFocusOut = (event) => {
          if (!this.contains(event.relatedTarget)) this.startAutoplay();
        };
        this.onVisibilityChange = () => {
          if (document.hidden) this.stopAutoplay();
          else this.startAutoplay();
        };
        this.onResize = () => this.updateControls();

        this.previousButton?.addEventListener('click', this.onPrevious);
        this.nextButton?.addEventListener('click', this.onNext);
        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.viewport);
        this.updateControls();
        this.startAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.resizeObserver?.disconnect();
        this.previousButton?.removeEventListener('click', this.onPrevious);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }

      get maxScroll() {
        return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      }

      get step() {
        if (!this.slides[0]) return this.viewport.clientWidth;
        const styles = window.getComputedStyle(this.querySelector('.popular-products__track'));
        return this.slides[0].getBoundingClientRect().width + (parseFloat(styles.columnGap) || 0);
      }

      move(direction, userInitiated = false) {
        if (this.maxScroll < 1 || this.isAnimating) return;

        this.normalizePosition();
        this.isAnimating = true;

        if (direction > 0) this.moveForward();
        else this.moveBackward();

        if (userInitiated) {
          this.stopAutoplay();
          this.startAutoplay();
        }
      }

      normalizePosition() {
        const slideStep = this.step;
        if (!slideStep) return;

        while (this.viewport.scrollLeft >= slideStep - 1) {
          const currentPosition = this.viewport.scrollLeft;
          const firstSlide = this.slides.shift();
          this.track.append(firstSlide);
          this.slides.push(firstSlide);
          this.viewport.scrollLeft = Math.max(0, currentPosition - slideStep);
        }
      }

      moveForward() {
        const slideStep = this.step;
        const behavior = this.reducedMotion.matches ? 'auto' : 'smooth';

        this.viewport.scrollTo({ left: slideStep, behavior });
        this.afterMovement(() => {
          const firstSlide = this.slides.shift();
          this.track.append(firstSlide);
          this.slides.push(firstSlide);
          this.viewport.scrollLeft = 0;
        });
      }

      moveBackward() {
        const slideStep = this.step;
        const lastSlide = this.slides.pop();

        this.track.prepend(lastSlide);
        this.slides.unshift(lastSlide);
        this.viewport.scrollLeft = slideStep;

        requestAnimationFrame(() => {
          this.viewport.scrollTo({ left: 0, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
          this.afterMovement();
        });
      }

      afterMovement(callback) {
        const delay = this.reducedMotion.matches ? 0 : 450;

        window.setTimeout(() => {
          callback?.();
          this.isAnimating = false;
        }, delay);
      }

      updateControls() {
        this.controls?.classList.toggle('is-hidden', this.maxScroll < 1);
      }

      startAutoplay() {
        if (!this.autoplayEnabled || this.reducedMotion.matches || document.hidden || this.maxScroll < 1) return;
        this.stopAutoplay();
        this.autoplayTimer = window.setInterval(() => this.move(1), this.autoplayDelay);
      }

      stopAutoplay() {
        if (!this.autoplayTimer) return;
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  );
}
